import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import { ROW_NO_HEAD, rowNoCell } from '../components/row-no';
import type { ScrollAnchor } from '@hm-spt/sola';

import * as api from '../api/client';
import { CatalogSidebar } from '../components/CatalogSidebar';
import { findMenuPlace, menuHeading, renderMenuTabs } from '../components/menu';
import { DetailDrawer } from '../components/DetailDrawer';
import { CatalogRowDrawer } from '../components/CatalogRowDrawer';
import { tableOrNull } from './catalog-requests/form';
import { Pager } from '../components/Pager';
import { assert } from '../lib/assert';
import { escapeHtml } from '../lib/cells';
import type { TableMetaFromServer } from '../types';
import { catalogPickOf, closeCatalogPickTab, listenCatalogPickSearch, sendCatalogPick, sendCatalogPickNone } from '../lib/catalog-pick';
import { detailOf } from '../lib/events';
import { hasTextSelection } from '../lib/selection';

interface CatalogSearchViewData {
  query: string;
  subtitle: string;
  status: 'idle' | 'loading' | 'ok' | 'err';
  bPick: boolean;   // pick 모드(`?pick=1`) — 등록 폼이 새 탭으로 연 것. 행 드로어에 [확인 — 등록 폼에 담기] 가 뜬다(skeleton 2026-10-22, 자리만)
}

/** 검색 결과의 요청 중 줄 source — 표 이름이 아니라 백엔드 UNION 이 붙인 라벨(search.queries.ts) */
const PENDING_SOURCE = 'pending';

/**
 * 🎯 CatalogSearchView — /catalog/_search 라우트
 *
 * 11개 카탈로그 테이블(섀시·CPU·GPU·NIC·HBA·HDD·SSD·메모리·PSU·CCM·LFF) 을
 * 한꺼번에 검색하는 통합 화면.
 *
 * 레이아웃은 CatalogView 와 동일 — topbar 에 검색 input, 메인이 그리드 100%.
 */
export class CatalogSearchView extends SolaView<CatalogSearchViewData> {
  private readonly sidebar = new CatalogSidebar();
  private readonly drawer = new DetailDrawer();
  private readonly rowDrawer = new CatalogRowDrawer();   // 카탈로그 표 17 의 행은 표 뷰어와 같은 수정 드로어([수정] · 변경 이력) — 입구마다 다르던 것(QA 2026-10-20 #21). 다른 표는 옛 읽기 드로어
  private readonly pager = new Pager();

  private tables: TableMetaFromServer[] = [];
  private tableByName = new Map<string, TableMetaFromServer>();

  // 현재 페이지의 행 (서버에서 받은 그대로 — 클라이언트 슬라이스 X)
  private currentHits: api.CatalogSearchHit[] = [];
  // 전체 매칭 개수 (페이지 무관) — pager 표시용
  private currentTotal = 0;

  // 서버 사이드 페이지네이션 상태
  private pageLimit = 50;
  private pageOffset = 0;

  // 데이터 로드 완료 시그널 — ready() Promise 용
  private readyResolve: (() => void) | null = null;
  private readyPromise: Promise<void>;

  // 검색 디바운스
  private searchTimer?: ReturnType<typeof setTimeout>;
  // 다시 찾기 채널 닫기 — pick 모드만 듣는다(2026-10-26)
  private stopListeningSearch: (() => void) | null = null;

  // ?q=&table= 로 들어온 열기 — 첫 검색 결과에서 그 표의 줄(없으면 유일한 줄)의 드로어를 한 번 연다(2026-10-20 — 표 페이지 대신 전체 검색에서 열어 카테고리를 옮겨도 길이 안 끊긴다)
  private readonly openTable: string;
  private bOpenHitOnce: boolean;

  override events = {
    input: { '@search': 'handleSearchInput' },
    click: {
      '@pick': 'handlePick',
      '@pick-none': 'handlePickNone',          // 줄 안 버튼 — Sola 는 첫 매칭만 실행(map 순서)이라 줄(@result-row)보다 앞에
      '@result-row': 'handleRowClick',
      '@clear': 'handleClearSearch',
    },
    'page:change': { '*': 'handlePageChange' },
  };

  /** q · table '' = 그냥 검색 화면. 요청 상세 · 전체 탭 · 제품 상세의 [카탈로그 수정] 이 `?q=PN&table=표` 로 연다 */
  constructor(params: { q: string; table: string; pick: boolean }) {
    super();
    this.openTable = params.table;
    this.bOpenHitOnce = params.q !== '' && !params.pick;   // pick 모드의 ?q= 는 폼의 찾기 칸 글 — 목록만, 드로어는 안 연다
    this.data = {
      query: params.q,
      subtitle: '검색어를 입력하면 11개 카탈로그를 동시에 검색합니다.',
      status: 'idle',
      bPick: params.pick,
    };
    this.readyPromise = new Promise<void>((resolve) => {
      this.readyResolve = resolve;
    });
  }

  // ===== 라우터 프로토콜 =====

  override getTitle(): string {
    return this.data.query
      ? `"${this.data.query}" 검색 — 카탈로그 — HCLTech`
      : '전체 검색 — 카탈로그 — HCLTech';
  }

  override async ready(): Promise<void> {
    return this.readyPromise;
  }

  override captureScrollAnchor(): ScrollAnchor {
    return {
      scrollY: window.scrollY,
      extra: { query: this.data.query },
    };
  }

  override restoreScrollAnchor(anchor: ScrollAnchor): void {
    const q = anchor.extra?.query as string | undefined;
    if (typeof q === 'string' && q !== '') {
      this.data.query = q;
      this.notify('query');
      const input = this._rootElement?.querySelector(
        '.search-input',
      ) as HTMLInputElement | null;
      if (input) input.value = q;
      void this.runSearch(q);
    }
    if (anchor.scrollY != null) {
      window.scrollTo(0, anchor.scrollY);
    }
  }

  // ===== 라이프사이클 =====

  override onMount(): void {
    this.rowDrawer.onRowChanged = () => { void this.fetchPage(this.data.query, this.pageOffset, this.pageLimit); };   // 저장 · 옮기기 뒤 결과 새로 — 옮기면 출처 표가 바뀐다
    void this.loadTables();
    if (this.data.query !== '') void this.runSearch(this.data.query);
    if (this.data.bPick) this.stopListeningSearch = listenCatalogPickSearch((search: api.CatalogPickSearch) => this.applyPickSearch(search));
  }

  override onUnmount(): void {
    super.onUnmount();
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (this.stopListeningSearch !== null) this.stopListeningSearch();
  }

  /** 폼이 보낸 다시 찾기 — 입력칸 글을 바꾸고 그 자리에서 검색(다시 읽기 없음). 치던 debounce 는 끈다 */
  private applyPickSearch(search: api.CatalogPickSearch): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.data.query = search.q;
    const input = this._rootElement?.querySelector<HTMLInputElement>('.search-input');
    if (input) input.value = search.q;
    void this.runSearch(search.q);
  }

  render(): HTMLElement {
    // 카탈로그 메뉴의 검색 탭 — 머리(제목 + 탭 줄)는 요청함과 같은 것, 그 아래 옛 검색 화면(29 표 목록 + 검색창 + 결과)이 통째로 몸통(A 안 4, 현민 2026-10-14)
    const place = findMenuPlace('/catalog');
    const head = place === null ? '' : `<header class="page-header"><h1>${menuHeading(place)}</h1>${renderMenuTabs(place)}</header>`;
    return htmlSolaWithInstance(this)`
      <div class="page page-catalog-frame pv-page">
        ${head}
        <div class="page-catalog page-catalog-search catalog-embedded">
        ${this.sidebar}
        <section class="catalog-main">
          <header class="catalog-topbar">
            <div class="catalog-topbar-left">
              <h1 class="page-title">🔍 전체 카탈로그 검색</h1>
              <span class="page-subtitle search-subtitle" data-bind="subtitle">${escapeHtml(this.data.subtitle)}</span>
            </div>
            <div class="catalog-topbar-right">
              <div class="search-wrap search-wrap-wide">
                <input
                  class="search-input"
                  type="search"
                  placeholder="모델명·option_part·키워드 (예: DL380 Gen11, 6442Y)"
                  autocomplete="off"
                  spellcheck="false"
                  autofocus
                  data-action="search"
                  value="${escapeHtml(this.data.query)}"
                />
              </div>
              <button class="btn-ghost" data-action="clear" title="검색어 지우기">✕</button>
              ${this.data.bPick ? '<button type="button" class="btn-ghost" data-action="pick-none" title="찾은 글을 등록 폼에 돌려보내고 이 탭을 닫습니다 — 폼에서 파트넘버 · 디스크립션 중 고릅니다">원하는 결과가 없음</button>' : ''}
            </div>
          </header>

          <section class="grid-wrap">
            <div class="grid-scroll">
              <table class="grid search-grid">
                <thead>
                  <tr>
                    ${ROW_NO_HEAD}
                    <th class="col-source">출처</th>
                    <th class="col-option-part">option_part</th>
                    <th class="col-extras">정보</th>
                    ${this.data.bPick ? '<th class="col-pick">등록 폼</th>' : ''}
                  </tr>
                </thead>
                <tbody class="search-results-tbody"></tbody>
              </table>
              <div class="grid-empty">검색어를 입력하세요.</div>
            </div>
          </section>
          ${this.pager}
        </section>
        ${this.drawer}
        ${this.rowDrawer}
        </div>
      </div>
    `;
  }

  // ===== 데이터 로딩 =====

  private async loadTables(): Promise<void> {
    try {
      const data = await api.fetchTables();
      this.tables = data.tables;
      this.tableByName = new Map(this.tables.map((t) => [t.name, t]));
      this.sidebar.setTables(this.tables);
      this.sidebar.setCurrent('_search');
      this.sidebar.setApiStatus('ok', `API 연결됨 · ${this.tables.length}개 테이블`);
    } catch (err) {
      this.sidebar.setApiStatus('err', 'API 연결 실패');
      this.setStatus('err', `테이블 목록 로드 실패: ${(err as Error).message}`);
    } finally {
      this.readyResolve?.();
    }
  }

  /** 신규 검색 — 첫 페이지부터 fetch. */
  private async runSearch(q: string): Promise<void> {
    const trimmed = q.trim();
    if (trimmed.length === 0) {
      this.currentHits = [];
      this.currentTotal = 0;
      this.pageOffset = 0;
      this.setStatus(
        'idle',
        '검색어를 입력하면 11개 카탈로그를 동시에 검색합니다.',
      );
      this.renderPage();
      return;
    }
    return this.fetchPage(trimmed, 0, this.pageLimit);
  }

  /**
   * 서버에 한 페이지 분량 fetch.
   * - runSearch (새 검색) — offset 0
   * - handlePageChange (페이지 이동) — 새 offset/limit
   */
  private async fetchPage(
    q: string,
    offset: number,
    limit: number,
  ): Promise<void> {
    this.setStatus('loading', `"${q}" 검색 중…`);
    // 페이지 전환 중에도 이전 결과는 잠시 유지하지 않고 비움 (혼동 방지)
    this.currentHits = [];
    this.renderPage();
    try {
      const result = await api.searchAll(q, offset, limit);
      this.currentHits = result.catalog;
      this.currentTotal = result.total;
      this.pageOffset = result.offset;
      this.pageLimit = result.limit;
      if (result.total === 0) {
        this.setStatus('ok', `"${q}" — 일치하는 카탈로그 행이 없습니다.`);
      } else {
        this.setStatus('ok', `${result.total}건`);
      }
      this.renderPage();
      this.openHitOnce();
    } catch (err) {
      this.setStatus('err', `검색 실패: ${(err as Error).message}`);
      this.currentHits = [];
      this.currentTotal = 0;
      this.renderPage();
    }
  }

  // ===== 렌더링 =====

  private setStatus(
    status: CatalogSearchViewData['status'],
    message: string,
  ): void {
    this.data.status = status;
    this.data.subtitle = message;
    const sub = this._rootElement?.querySelector('.search-subtitle');
    if (sub) {
      sub.textContent = message;
      sub.className = `page-subtitle search-subtitle status-${status}`;
    }
  }

  /**
   * 현재 페이지 행 렌더 + pager 상태 갱신.
   * (서버가 이미 페이지 단위로 잘라줘서 클라이언트 슬라이스 없음)
   */
  private renderPage(): void {
    this.pager.setState(this.currentTotal, this.pageLimit, this.pageOffset);

    const tbody = this._rootElement?.querySelector('.search-results-tbody');
    const empty = this._rootElement?.querySelector('.grid-empty');
    if (!tbody) return;

    if (this.currentHits.length === 0) {
      tbody.innerHTML = '';
      if (empty) {
        empty.classList.remove('hidden');
        empty.textContent =
          this.data.status === 'idle'
            ? '검색어를 입력하세요.'
            : this.data.status === 'loading'
              ? '검색 중…'
              : '일치하는 행이 없습니다.';
      }
      return;
    }

    if (empty) empty.classList.add('hidden');

    const rows = this.currentHits
      .map((hit, idx) => {
        const sourceLabel = this.labelForSource(hit.source);
        const extras = this.formatExtras(hit);
        return `
          <tr class="result-row" data-action="result-row" data-idx="${idx}">
            ${rowNoCell(this.pageOffset, idx)}
            <td class="col-source">
              <span class="chip source-chip" title="${escapeHtml(hit.source)}">${escapeHtml(sourceLabel)}</span>
            </td>
            <td class="col-option-part">
              <span class="cell-mono">${escapeHtml(hit.option_part)}</span>
            </td>
            <td class="col-extras">${extras}</td>
            ${this.data.bPick ? `<td class="col-pick">${pickButtonOf(hit, idx)}</td>` : ''}
          </tr>
        `;
      })
      .join('');

    tbody.innerHTML = rows;
  }

  /** source(테이블명) → 표시용 label. tableByName 에 없으면 source 그대로. */
  private labelForSource(source: string): string {
    return this.tableByName.get(source)?.label ?? source;
  }

  /**
   * extra_* 컬럼들을 "key value · key value" 형태 HTML 로 합성.
   * extra_ 접두사는 제거하여 보여줌. 빈 값은 skip.
   */
  private formatExtras(hit: api.CatalogSearchHit): string {
    const parts: string[] = [];
    for (const [key, val] of Object.entries(hit)) {
      if (!key.startsWith('extra_')) continue;
      if (val == null || val === '') continue;
      const shortKey = key.slice('extra_'.length);
      parts.push(
        `<span class="extra-pair"><span class="extra-key">${escapeHtml(shortKey)}</span> ${escapeHtml(val)}</span>`,
      );
    }
    if (parts.length === 0) return '<span class="cell-empty">—</span>';
    return parts.join(' <span class="extra-sep">·</span> ');
  }

  // ===== 이벤트 핸들러 =====

  handleSearchInput(_e: Event, target: HTMLElement): void {
    const q = (target as HTMLInputElement).value;
    this.data.query = q;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.searchTimer = undefined;
      void this.runSearch(q);
    }, 280);
  }

  handleClearSearch(): void {
    this.data.query = '';
    const input = this._rootElement?.querySelector(
      '.search-input',
    ) as HTMLInputElement | null;
    if (input) {
      input.value = '';
      input.focus();
    }
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.currentHits = [];
    this.currentTotal = 0;
    this.pageOffset = 0;
    this.setStatus(
      'idle',
      '검색어를 입력하면 11개 카탈로그를 동시에 검색합니다.',
    );
    this.renderPage();
  }

  /**
   * 페이지 변경 시 서버에서 해당 페이지 fetch.
   * 현재 검색어 (this.data.query) 그대로 사용.
   */
  handlePageChange(e: Event): void {
    const detail = detailOf(e, 'page:change');
    const q = this.data.query.trim();
    if (!q) return;
    void this.fetchPage(q, detail.offset, detail.limit);
    // 페이지 변경 시 그리드 영역 스크롤 위로
    const scroll = this._rootElement?.querySelector('.grid-scroll');
    if (scroll) scroll.scrollTop = 0;
  }

  /** ?q= 로 들어온 첫 결과 — 그 표(table)의 줄, 표가 안 맞거나 비었으면 유일한 줄. 둘 다 아니면 목록만. 한 번만 */
  private openHitOnce(): void {
    if (!this.bOpenHitOnce) return;
    this.bOpenHitOnce = false;
    const sameTable = this.currentHits.filter((hit) => hit.source === this.openTable);
    const hit = sameTable.length === 1 ? sameTable[0] : this.currentHits.length === 1 ? this.currentHits[0] : undefined;
    if (hit === undefined) return;
    void this.openHit(hit);
  }

  async handleRowClick(e: Event, target: HTMLElement): Promise<void> {
    if (hasTextSelection()) return;   // 긁어 복사(드래그)면 안 연다 — 클릭과 가른다(2026-10-30 전수)
    if ((e.target as HTMLElement).closest('a') !== null) return;   // 이어진 제품 링크는 제 길로(새 탭) — 드로어는 안 연다
    const tr = target.closest('tr.result-row') as HTMLElement | null;
    if (!tr) return;
    const idx = Number(tr.dataset.idx);
    if (!Number.isInteger(idx)) return;
    const hit = this.currentHits[idx];
    if (!hit) return;
    await this.openHit(hit);
    void e;
  }

  /** 결과 줄 하나의 드로어 — 표 17 이면 수정 드로어, 아니면 옛 읽기 드로어. 줄 누르기 · ?q= 열기가 같이 쓴다 */
  /**
   * pick 모드 결과 줄의 [선택] — 약속: 그 줄의 행을 받아(`fetchCatalogRow` — 검색 결과엔 id 가 없다) `catalogPickOf` 로 만들고
   * `sendCatalogPick` 으로 방송한 뒤 `closeCatalogPickTab()`(폼 탭에 포커스 → 닫기). 폼이 연 탭이라 닫힌다(주소로 직접 연 탭은 안 닫힘 — 그때는 그대로 둔다). 행을 못 받으면 옛 읽기 드로어에 이유.
   */
  /** pick 모드 결과 표 위 [원하는 결과가 없음] — 약속: 지금 찾기 글을 sendCatalogPickNone 으로 보내고 closeCatalogPickTab(). 폼이 못 찾은 글 안내를 띄운다 */
  handlePickNone(): void {
    sendCatalogPickNone(this.data.query.trim());
    closeCatalogPickTab();
  }

  async handlePick(_e: Event, target: HTMLElement): Promise<void> {
    const hit = this.currentHits[Number(target.dataset.idx)];
    assert(hit !== undefined, '[선택] 은 결과 줄 안에만 그려진다 — data-idx 는 currentHits 의 자리(renderResults)');
    const table = tableOrNull(hit.source);
    assert(table !== null, '[선택] 은 표 17 의 줄에만 그려진다 — pickButtonOf 가 걸렀다');
    const row = await api.fetchCatalogRow(hit.source, hit.option_part);
    if (row === null) {
      this.drawer.open({ ...hit, source: hit.source, option_part: hit.option_part }, `${this.labelForSource(hit.source)} — 상세 row 없음 · 담지 못했습니다`);
      return;
    }
    sendCatalogPick(catalogPickOf(table, row));
    closeCatalogPickTab();
  }

  private async openHit(hit: api.CatalogSearchHit): Promise<void> {
    const sourceLabel = this.labelForSource(hit.source);
    const table = tableOrNull(hit.source);
    // 표 17 밖의 줄만 옛 읽기 드로어를 먼저 띄운다(피드백). 표 17 은 행 id 를 받아 수정 드로어 하나만 — 둘을 겹쳐 열면 드로어가 두 번 뜨는 것으로 보였다(QA 2026-10-21)
    if (table === null) {
      this.drawer.open(
        { ...hit, source: hit.source, option_part: hit.option_part },
        `${sourceLabel} — 로딩 중…`,
      );
    }

    // 해당 카탈로그 테이블에서 full row fetch 후 드로어
    try {
      const fullRow = await api.fetchCatalogRow(hit.source, hit.option_part);
      const rowId = fullRow === null ? NaN : Number(fullRow.id);
      if (fullRow && table !== null && Number.isInteger(rowId) && rowId > 0) {
        void this.rowDrawer.open(table, rowId);   // 표 17 — 수정 드로어
      } else if (fullRow) {
        this.drawer.open(fullRow, sourceLabel);
      } else {
        this.drawer.open(
          { ...hit, source: hit.source, option_part: hit.option_part },
          `${sourceLabel} — 상세 row 없음`,
        );
      }
    } catch (err) {
      this.drawer.open(
        {
          // spread 가 먼저다 — 뒤에 두면 아래 세 속성이 전부 덮여 error 가 사라진다.
          ...hit,
          source: hit.source,
          option_part: hit.option_part,
          error: (err as Error).message,
        },
        `${sourceLabel} — 로드 실패`,
      );
    }
  }
}

/** pick 모드 결과 줄의 [선택] — 표 17 의 줄에만(요청 · 옛 표 줄은 담을 행이 없다). 검색하자마자 보인다 — 드로어까지 안 들어간다(현민 2026-10-22).
 *  이미 제품이 이어진 행은 [선택] 대신 그 제품(새 탭) — 제품과 카탈로그 행은 1:1 이라 다시 이을 수 없다(현민 2026-10-28) */
function pickButtonOf(hit: api.CatalogSearchHit, idx: number): string {
  if (tableOrNull(hit.source) === null) return pickBlockedOf(hit.source);
  if (hit.linked_product !== null) {
    return `<a class="pick-linked" href="/products/${escapeHtml(hit.linked_product.id)}" target="_blank" rel="noopener" title="이 행은 이미 제품에 이어져 있어 고를 수 없습니다 — 누르면 그 제품(새 탭)">제품 ${escapeHtml(hit.linked_product.hclt_number)} ↗</a>`;
  }
  return `<button type="button" class="btn-primary pick-btn" data-action="pick" data-idx="${idx}" title="이 행을 등록 폼에 담고 이 탭을 닫습니다">선택</button>`;
}

/** 표 17 밖의 줄(요청 중 · 옛 표)은 빈칸 대신 "선택 불가" — 빈칸은 고장으로 읽힌다(현민 2026-11-03).
 *  검색 결과엔 요청 번호가 없어 [요청 열기] 는 못 건다 — 요청함에서 승격한 뒤 고른다 */
function pickBlockedOf(source: string): string {
  const bPending: boolean = source === PENDING_SOURCE;
  const word: string = bPending ? '선택 불가 · 요청 중' : '선택 불가';
  const why: string = bPending
    ? '아직 요청 중인 번호라 담을 카탈로그 행이 없습니다 — 요청함에서 승격한 뒤 고릅니다'
    : '확정된 카탈로그 행이 아니라 담을 수 없습니다';
  return `<span class="pick-blocked" title="${why}">${word}</span>`;
}
